/**
 * Item Service - 业务逻辑层 + Mock 适配
 * 页面组件只调用此 Service 层,不直接调用 API
 */

import { config } from '../../config';
import * as itemMock from '../../mocks/master-data/item';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';
import type { ApiResponse, PageData } from '../../types/common';
import type {
  ItemDTO,
  ItemDetailDTO,
  QueryItemPageRequest,
  UpdateItemRequest,
  UpdateWeightLimitRequest,
  SyncItemRequest,
  SyncResult,
} from '../../types/master-data/item';

/**
 * 分页查询商品列表
 */
export async function pageItem(
  params: QueryItemPageRequest
): Promise<ApiResponse<PageData<ItemDTO>>> {
  if (config.mock) {
    return itemMock.mockPageItem(params);
  }

  return httpClient.post(API_ENDPOINTS.ITEM.PAGE, params);
}

/**
 * 查询商品详情
 */
export async function getItemByCode(
  ownerCode: string,
  code: string
): Promise<ApiResponse<ItemDetailDTO>> {
  if (config.mock) {
    return itemMock.mockGetItemBySku(code);
  }

  return httpClient.get(`${API_ENDPOINTS.ITEM.BASE}/${ownerCode}/${code}`);
}

/**
 * 编辑商品称重上下限
 */
export async function updateWeightLimit(
  params: UpdateWeightLimitRequest
): Promise<ApiResponse<ItemDTO>> {
  if (config.mock) {
    return itemMock.mockUpdateWeightLimit(params);
  }

  return httpClient.post(API_ENDPOINTS.ITEM.UPDATE_WEIGHT_LIMIT, params);
}

/**
 * 更新商品信息
 */
export async function updateItem(
  params: UpdateItemRequest
): Promise<ApiResponse<ItemDTO>> {
  if (config.mock) {
    return itemMock.mockUpdateItem(params);
  }

  return httpClient.post(API_ENDPOINTS.ITEM.UPDATE, params);
}

/**
 * 手动触发商品主数据同步
 */
export async function syncItem(
  params: SyncItemRequest
): Promise<ApiResponse<SyncResult>> {
  if (config.mock) {
    return itemMock.mockSyncItem(params);
  }

  return httpClient.post(API_ENDPOINTS.ITEM.SYNC, params);
}

/**
 * 导出商品数据
 */
export async function exportItem(
  params: { status?: string; itemCodes?: string[]; code?: string; name?: string; ownerCode?: string; brandCode?: string }
): Promise<ApiResponse<string>> {
  return httpClient.post(API_ENDPOINTS.ITEM.EXPORT, params);
}

/**
 * 导入货品 CSV 文件（模拟 SFTP 文件传输）
 * 上传 CSV → OSS → 发送 MQ → Processor 消费入库
 */
export async function importItemFile(
  file: File
): Promise<ApiResponse<{ downloadUrl: string; fileName: string } | null>> {
  const formData = new FormData();
  formData.append('file', file);
  return httpClient.post(API_ENDPOINTS.ITEM.IMPORT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * 商品下拉选项（模糊搜索）
 */
export async function itemOption(
  params: { code?: string }
): Promise<ApiResponse<Array<{ value: string; label: string }>>> {
  if (config.mock) {
    return Promise.resolve({
      success: true,
      errorMessage: null,
      flag: 'success',
      data: [],
      traceId: `mock-${Date.now()}`,
    });
  }
  return httpClient.post(API_ENDPOINTS.ITEM.OPTION, params);
}
